import React, { useState, useEffect, useRef } from 'react';
import { outboundApi, mailAccountApi, contactApi } from '../api/client';

const MAX_FILES = 10;
const MAX_FILE_MB = 10;
const MAX_TOTAL_MB = 20;

// Menschenlesbare Bezeichnungen für Backend-Feldnamen, damit Fehlermeldungen
// vom Server (z.B. "Dieses Feld darf nicht leer sein.") das betroffene Feld
// konkret benennen, statt nur die erste beliebige Fehlermeldung anzuzeigen.
const FIELD_LABELS = {
  mail_account: 'Absenderkonto (Von)',
  to_addresses: 'Empfänger (An)',
  bcc_addresses: 'BCC',
  subject: 'Betreff',
  body_plain: 'Nachricht',
  in_reply_to_message: 'Bezug (Antwort auf)',
  attachment_ids: 'Weiterzuleitende Anhänge',
  new_attachments: 'Anhänge',
};

function formatSize(bytes) {
  return bytes > 1048576 ? (bytes / 1048576).toFixed(1) + ' MB' : Math.round(bytes / 1024) + ' KB';
}

export default function ComposeModal({ onClose, replyTo, forwardOf }) {
  replyTo = replyTo || null;
  forwardOf = forwardOf || null;

  const [accounts, setAccounts] = useState([]);
  const [form, setForm] = useState({
    mail_account: '', to_addresses: '', subject: '', body_plain: '',
  });
  const [selectedAttIds, setSelectedAttIds] = useState([]);
  const [newFiles, setNewFiles] = useState([]);
  const [fileError, setFileError] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [success, setSuccess] = useState(false);

  // Adress-Autovervollständigung im An-Feld (Kontaktliste, siehe PortalSettings)
  const [toSuggestions, setToSuggestions] = useState([]);
  const [showToSuggestions, setShowToSuggestions] = useState(false);
  const [activeSuggestion, setActiveSuggestion] = useState(0);
  const toDebounceRef = useRef(null);

  const fieldRefs = {
    mail_account: useRef(null),
    to_addresses: useRef(null),
    subject: useRef(null),
    body_plain: useRef(null),
  };

  const isReply = !!replyTo && !forwardOf;
  const isForward = !!forwardOf;

  useEffect(() => {
    if (isReply && replyTo) {
      const subj = replyTo.subject || '';
      setForm(f => ({
        ...f,
        to_addresses: replyTo.sender || '',
        subject: subj.startsWith('Re:') ? subj : 'Re: ' + subj,
        body_plain: '\n\n--- Urspruengliche Nachricht ---\nVon: ' + replyTo.sender + '\nBetreff: ' + subj + '\n\n' + (replyTo.body_plain || replyTo.body_preview || ''),
      }));
    } else if (isForward && forwardOf) {
      const subj = forwardOf.subject || '';
      setForm(f => ({
        ...f,
        to_addresses: '',
        subject: subj.startsWith('Fwd:') ? subj : 'Fwd: ' + subj,
        body_plain: '\n\n--- Weitergeleitet ---\nVon: ' + forwardOf.sender + '\nBetreff: ' + subj + '\n\n' + (forwardOf.body_plain || forwardOf.body_preview || ''),
      }));
      if (forwardOf.attachments && forwardOf.attachments.length > 0) {
        setSelectedAttIds(forwardOf.attachments.map(a => a.id));
      }
    }
  }, [replyTo, forwardOf, isReply, isForward]);

  useEffect(() => {
    mailAccountApi.list().then(res => {
      const list = res.data.results || res.data;
      setAccounts(list);
      if (list.length > 0) {
        const src = forwardOf || replyTo;
        const pref = src ? (list.find(a => a.id === src.mail_account) || list[0]) : list[0];
        setForm(f => ({ ...f, mail_account: pref.id }));
      }
    });
  }, [replyTo, forwardOf]);

  function toggleAtt(id) {
    setSelectedAttIds(prev =>
      prev.indexOf(id) >= 0 ? prev.filter(x => x !== id) : prev.concat([id])
    );
  }

  function handleFilesSelected(e) {
    const picked = Array.from(e.target.files || []);
    e.target.value = ''; // erneutes Auswaehlen derselben Datei ermoeglichen
    if (picked.length === 0) return;
    setFileError('');
    setNewFiles(prev => {
      const combined = [...prev, ...picked];
      if (combined.length > MAX_FILES) {
        setFileError(`Maximal ${MAX_FILES} Anhaenge pro Mail.`);
        return prev;
      }
      const tooBig = picked.find(f => f.size > MAX_FILE_MB * 1024 * 1024);
      if (tooBig) {
        setFileError(`"${tooBig.name}" ist groesser als ${MAX_FILE_MB} MB.`);
        return prev;
      }
      const totalSize = combined.reduce((s, f) => s + f.size, 0);
      if (totalSize > MAX_TOTAL_MB * 1024 * 1024) {
        setFileError(`Anhaenge insgesamt groesser als ${MAX_TOTAL_MB} MB.`);
        return prev;
      }
      return combined;
    });
  }

  function removeFile(idx) {
    setFileError('');
    setNewFiles(prev => prev.filter((_, i) => i !== idx));
  }

  const totalNewFilesSize = newFiles.reduce((s, f) => s + f.size, 0);

  // Fokussiert das erste fehlerhafte Feld und lässt es kurz sichtbar rot
  // umrandet erscheinen (siehe fieldErrors-gesteuerte Rahmenfarbe im JSX).
  function focusField(fieldName) {
    const ref = fieldRefs[fieldName];
    if (ref && ref.current) {
      ref.current.focus();
      if (ref.current.scrollIntoView) {
        ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }

  function failClientValidation(fieldName, message) {
    setFieldErrors({ [fieldName]: message });
    setError(`${FIELD_LABELS[fieldName] || fieldName}: ${message}`);
    focusField(fieldName);
  }

  // ── Adress-Autovervollständigung (An-Feld) ──────────────────
  // Das Feld kann mehrere durch Komma getrennte Adressen enthalten — die
  // Vervollständigung bezieht sich immer nur auf das zuletzt getippte Segment
  // (nach dem letzten Komma), damit bereits eingetragene Empfänger erhalten
  // bleiben.
  function currentAddressToken(value) {
    const parts = value.split(',');
    return parts[parts.length - 1].trim();
  }

  function handleToChange(e) {
    const value = e.target.value;
    setForm(f => ({ ...f, to_addresses: value }));
    if (fieldErrors.to_addresses) setFieldErrors(prev => { const next = { ...prev }; delete next.to_addresses; return next; });

    if (toDebounceRef.current) clearTimeout(toDebounceRef.current);
    const token = currentAddressToken(value);
    if (token.length < 3) {
      setToSuggestions([]);
      setShowToSuggestions(false);
      return;
    }
    toDebounceRef.current = setTimeout(async () => {
      try {
        const { data } = await contactApi.autocomplete(token);
        setToSuggestions(data);
        setActiveSuggestion(0);
        setShowToSuggestions(data.length > 0);
      } catch {
        setToSuggestions([]);
        setShowToSuggestions(false);
      }
    }, 250);
  }

  function selectToSuggestion(contact) {
    const label = contact.display_name ? `${contact.display_name} <${contact.email}>` : contact.email;
    setForm(f => {
      const value = f.to_addresses;
      const lastComma = value.lastIndexOf(',');
      const prefix = lastComma >= 0 ? value.slice(0, lastComma + 1) + ' ' : '';
      return { ...f, to_addresses: prefix + label + ', ' };
    });
    setToSuggestions([]);
    setShowToSuggestions(false);
    setTimeout(() => fieldRefs.to_addresses.current && fieldRefs.to_addresses.current.focus(), 0);
  }

  function handleToKeyDown(e) {
    if (!showToSuggestions || toSuggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveSuggestion(i => Math.min(i + 1, toSuggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveSuggestion(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      selectToSuggestion(toSuggestions[activeSuggestion] || toSuggestions[0]);
    } else if (e.key === 'Escape') {
      setShowToSuggestions(false);
    }
  }

  async function handleSend() {
    setFieldErrors({});

    // Sicherheitsnetz gegen Browser-Autofill/Kontaktvorschläge (v.a. auf dem
    // iPhone beobachtet): manche mobilen Browser setzen den sichtbaren
    // DOM-Wert eines Feldes, ohne das von React erwartete change-Event
    // auszulösen. Der React-State (form.*) bleibt dann auf einem älteren
    // Stand, obwohl das Feld sichtbar ausgefüllt ist -- Validierung und
    // Versand liefen dadurch mit leeren Pflichtfeldern, die der Server dann
    // zurückwies ("Dieses Feld ist erforderlich."), obwohl der Nutzer sie
    // sichtbar ausgefüllt hatte. Deshalb hier direkt vor dem Senden die
    // tatsächlichen, aktuell im DOM stehenden Werte auslesen und als
    // Grundlage für Validierung + Versand verwenden statt blind dem
    // React-State zu vertrauen.
    const sendForm = { ...form };
    for (const field of ['mail_account', 'to_addresses', 'subject', 'body_plain']) {
      const domValue = fieldRefs[field].current && fieldRefs[field].current.value;
      if (domValue && domValue !== sendForm[field]) sendForm[field] = domValue;
    }
    if (sendForm.mail_account !== form.mail_account || sendForm.to_addresses !== form.to_addresses ||
      sendForm.subject !== form.subject || sendForm.body_plain !== form.body_plain) {
      setForm(sendForm);
    }

    if (!sendForm.mail_account) { failClientValidation('mail_account', 'Bitte ein Absenderkonto wählen.'); return; }
    if (!sendForm.to_addresses.trim()) { failClientValidation('to_addresses', 'Bitte mindestens einen Empfänger angeben.'); return; }
    if (!sendForm.subject.trim()) { failClientValidation('subject', 'Bitte einen Betreff angeben.'); return; }
    if (!sendForm.body_plain.trim()) { failClientValidation('body_plain', 'Bitte einen Nachrichtentext eingeben.'); return; }
    setSending(true);
    setError('');
    try {
      const fd = new FormData();
      Object.entries(sendForm).forEach(([key, value]) => fd.append(key, value));
      if (isReply) fd.append('in_reply_to_message', replyTo.id);
      if (isForward) selectedAttIds.forEach(id => fd.append('attachment_ids', id));
      newFiles.forEach(file => fd.append('new_attachments', file));
      await outboundApi.send(fd);
      setSuccess(true);
      setTimeout(onClose, 1500);
    } catch (e) {
      const d = e.response && e.response.data;
      if (d && d.detail && typeof d.detail === 'string') {
        // Nicht-feldbezogener Fehler (z.B. Server-/SMTP-Problem) — keine
        // Feld-Zuordnung möglich, generische Meldung unverändert anzeigen.
        setError(d.detail);
      } else if (d && typeof d === 'object') {
        // DRF-Validierungsfehler: {"feldname": ["Meldung", ...], ...} — für
        // JEDES betroffene Feld die Meldung mit ausgeschriebenem Feldnamen
        // zusammenstellen (statt nur die erste beliebige Meldung ohne
        // Kontext anzuzeigen), und das erste fehlerhafte Feld fokussieren.
        const newFieldErrors = {};
        const lines = [];
        Object.entries(d).forEach(([key, val]) => {
          const msg = Array.isArray(val) ? val.join(' ') : String(val);
          newFieldErrors[key] = msg;
          const label = FIELD_LABELS[key] || key;
          lines.push(`${label}: ${msg}`);
        });
        setFieldErrors(newFieldErrors);
        setError(lines.length > 0 ? lines.join('\n') : 'Fehler beim Senden.');
        const firstErrorField = Object.keys(newFieldErrors).find(k => fieldRefs[k]);
        if (firstErrorField) focusField(firstErrorField);
      } else {
        setError('Fehler beim Senden.');
      }
    } finally {
      setSending(false);
    }
  }

  const setField = field => e => {
    setForm(f => ({ ...f, [field]: e.target.value }));
    if (fieldErrors[field]) setFieldErrors(prev => { const next = { ...prev }; delete next[field]; return next; });
  };
  const fieldClass = (field, base) => fieldErrors[field]
    ? `${base} border-red-400 focus:ring-red-400`
    : `${base} border-gray-300 focus:ring-blue-500`;
  const title = isForward ? 'Weiterleiten' : isReply ? 'Antworten' : 'Neue E-Mail';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4 flex flex-col" style={{maxHeight:'90vh'}}>
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-base font-semibold text-gray-900">{title}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-lg">X</button>
        </div>
        <div className="flex-1 overflow-auto p-6 space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Von</label>
            <select ref={fieldRefs.mail_account} value={form.mail_account} onChange={setField('mail_account')}
              className={fieldClass('mail_account', 'w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:outline-none')}>
              <option value="">- Konto waehlen -</option>
              {accounts.map(a => (
                <option key={a.id} value={a.id}>
                  {a.display_name ? a.display_name + ' <' + a.email_address + '>' : a.email_address}
                </option>
              ))}
            </select>
            {fieldErrors.mail_account && <p className="text-xs text-red-600 mt-1">{fieldErrors.mail_account}</p>}
          </div>
          <div className="relative">
            <label className="block text-xs font-medium text-gray-500 mb-1">An</label>
            <input ref={fieldRefs.to_addresses} type="text" value={form.to_addresses}
              onChange={handleToChange}
              onKeyDown={handleToKeyDown}
              onFocus={() => { if (toSuggestions.length > 0) setShowToSuggestions(true); }}
              onBlur={() => setTimeout(() => setShowToSuggestions(false), 150)}
              autoComplete="off"
              placeholder="empfaenger@beispiel.de"
              className={fieldClass('to_addresses', 'w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:outline-none')} />
            {fieldErrors.to_addresses && <p className="text-xs text-red-600 mt-1">{fieldErrors.to_addresses}</p>}
            {showToSuggestions && toSuggestions.length > 0 && (
              <div className="absolute z-10 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-auto">
                {toSuggestions.map((c, idx) => (
                  <button type="button" key={c.id}
                    onMouseDown={(e) => { e.preventDefault(); selectToSuggestion(c); }}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 ${idx === activeSuggestion ? 'bg-blue-50' : ''}`}>
                    {c.display_name ? (
                      <>
                        <span className="font-medium text-gray-800">{c.display_name}</span>{' '}
                        <span className="text-gray-400">&lt;{c.email}&gt;</span>
                      </>
                    ) : (
                      <span className="text-gray-800">{c.email}</span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Betreff</label>
            <input ref={fieldRefs.subject} type="text" value={form.subject} onChange={setField('subject')}
              placeholder="Betreff"
              className={fieldClass('subject', 'w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:outline-none')} />
            {fieldErrors.subject && <p className="text-xs text-red-600 mt-1">{fieldErrors.subject}</p>}
          </div>
          {isForward && forwardOf && forwardOf.attachments && forwardOf.attachments.length > 0 && (
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-2">Anhaenge weiterleiten</label>
              <div className="space-y-1 border border-gray-200 rounded-lg p-3 bg-gray-50">
                {forwardOf.attachments.map(att => (
                  <label key={att.id} className="flex items-center gap-3 cursor-pointer hover:bg-white rounded px-2 py-1">
                    <input type="checkbox"
                      checked={selectedAttIds.indexOf(att.id) >= 0}
                      onChange={() => toggleAtt(att.id)}
                      className="rounded border-gray-300 text-blue-600" />
                    <span className="text-sm text-gray-800 truncate flex-1">{att.filename}</span>
                    <span className="text-xs text-gray-400 whitespace-nowrap">
                      {att.size_bytes > 1048576 ? (att.size_bytes/1048576).toFixed(1)+' MB' : Math.round(att.size_bytes/1024)+' KB'}
                    </span>
                  </label>
                ))}
              </div>
              <p className="text-xs text-gray-400 mt-1">{selectedAttIds.length} von {forwardOf.attachments.length} ausgewaehlt</p>
            </div>
          )}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-2">Anhaenge</label>
            <label className="inline-flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 cursor-pointer">
              📎 Datei(en) auswaehlen
              <input type="file" multiple onChange={handleFilesSelected} className="hidden" />
            </label>
            <span className="ml-2 text-xs text-gray-400">
              Max. {MAX_FILES} Dateien, je bis {MAX_FILE_MB} MB, insgesamt bis {MAX_TOTAL_MB} MB
            </span>
            {newFiles.length > 0 && (
              <div className="mt-2 space-y-1 border border-gray-200 rounded-lg p-3 bg-gray-50">
                {newFiles.map((file, idx) => (
                  <div key={idx} className="flex items-center justify-between gap-2">
                    <span className="text-sm text-gray-800 truncate flex-1">{file.name}</span>
                    <span className="text-xs text-gray-400 whitespace-nowrap">{formatSize(file.size)}</span>
                    <button type="button" onClick={() => removeFile(idx)}
                      className="text-gray-400 hover:text-red-600 text-sm px-1" title="Entfernen">✕</button>
                  </div>
                ))}
                <p className="text-xs text-gray-400 pt-1">{newFiles.length} Datei{newFiles.length !== 1 ? 'en' : ''}, {formatSize(totalNewFilesSize)} gesamt</p>
              </div>
            )}
            {fileError && <p className="text-xs text-red-600 mt-1">{fileError}</p>}
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Nachricht</label>
            <textarea ref={fieldRefs.body_plain} value={form.body_plain} onChange={setField('body_plain')}
              rows={isReply || isForward ? 10 : 8}
              placeholder="Ihre Nachricht..."
              className={fieldClass('body_plain', 'w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:outline-none resize-none font-mono')} />
            {fieldErrors.body_plain && <p className="text-xs text-red-600 mt-1">{fieldErrors.body_plain}</p>}
          </div>
          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 whitespace-pre-line">
              {error}
            </div>
          )}
          {success && <div className="text-sm text-green-600 bg-green-50 border border-green-200 rounded-lg px-3 py-2">E-Mail wird gesendet...</div>}
        </div>
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">Abbrechen</button>
          <button onClick={handleSend} disabled={sending || success}
            className="px-5 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50">
            {sending ? 'Wird gesendet...' : 'Senden'}
          </button>
        </div>
      </div>
    </div>
  );
}
